import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { MessageDisplayService } from '@shared/message-display/message-display.service';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';
import { AuthService } from '@app/shared/services/auth.service';
import { ExportService } from '@app/shared/services/export.service';

@Injectable()
export class UserService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private messageDisplayService: MessageDisplayService,
    private exportService: ExportService,
    private translate: TranslateService
  ) {}

  private handleErrors = (error: any): Observable<any> => {
    this.messageDisplayService.error(error.message);
    return throwError(error);
  };

  get_users(search: string): Observable<any> {
    let url = environment.apiUrl + '/user/';
    if (search) {
      url += `?search=${search}`;
    }
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<any>(url, { params: params })
      .pipe(catchError(this.handleErrors));
  }

  export_db_users() {
    let url = environment.apiUrl + '/user/all';
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<any>(url, { params: params })
      .pipe(catchError(this.handleErrors));
  }

  get_user_by_email(email: string): Observable<any> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<any>(environment.apiUrl + `/user/${email}`, { params: params })
      .pipe(catchError(this.handleErrors));
  }

  create_user(user: any): Observable<any> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .post<any>(environment.apiUrl + '/user/', user, { params: params })
      .pipe(
        catchError((error: HttpErrorResponse): Observable<any> => {
          if (
            error.status === 403 &&
            error.error.toString().includes('USER_ALREADY_EXISTS')
          ) {
            this.messageDisplayService.warn(
              this.translate.instant('key__error_user_already_exists')
            );
            return throwError(error);
          } else if (
            error.status === 403 &&
            error.error.toString().includes('USER_NOT_FOUND')
          ) {
            this.messageDisplayService.warn(
              this.translate.instant('key__error_user_not_found')
            );
            return throwError(error);
          }
          return this.handleErrors(error);
        })
      );
  }

  update_user(user: any): Observable<any> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .put<any>(environment.apiUrl + `/user/${user.email}`, user, {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  changeAccountHolder(user: any, newHolder: string): Observable<any> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    const payload = {
      user,
      new: newHolder,
    };
    return this.http
      .post<any>(environment.apiUrl + `/user/account-holder`, payload, {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  delete_user(email: string): Observable<any> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .delete<any>(environment.apiUrl + `/user/${email}`, { params: params })
      .pipe(catchError(this.handleErrors));
  }

  autocomplete_user(email: string): Observable<any> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<any>(environment.apiUrl + `/user/autocomplete/${email}`, {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }
}
